import { Box } from '@mui/material';
import dayjs from 'dayjs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Rectangle,
  ReferenceLine,
} from 'recharts';
import { FlexBox } from 'components/Containers/FlexBox';
import { ThemePalette } from 'mui.theme';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { getDataUnit } from 'views/Production/Hauler/component/utils';
import { truncateTo2dp } from 'utils/helpers/general';
import { useAppSelector } from 'store/hook';
import { formatNumberToShortScale } from 'views/Production/Loader/component/utils';
import { useMemo } from 'react';
import { DATE_FORMAT } from 'utils/constants/dateTime';
import { convertStdDateString } from 'utils/helpers/dateTime';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  selectedSource: string;
  customerCode: string;
  unitSystem: string;
  device: any;
  height?: string;
  aggregation: string;
}

const AssetItemChart = ({
  enableDarkTheme,
  selectedSource,
  customerCode,
  unitSystem,
  device,
  height = '230px',
  aggregation,
}: Props) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    data: {
      loaders: { devices },
    },
  } = useAppSelector((state) => state.productionReducer);

  let dataUnit = getDataUnit(selectedSource, unitSystem);
  dataUnit = dataUnit ? `(${dataUnit})` : '';

  const formattedChartData = devices
    .filter((item) => item.loader === device.loader)
    .map((entry) => {
      let value = 0;

      switch (selectedSource) {
        case 'Production':
          // value = deviceSummary.find((item) => item.loader === entry.loader)
          //   ?.total_load_for_selected_range as number;
          value = entry.total_load_for_selected_range;
          break;
        case 'Production Per Hour':
          value = entry.device_loads_per_hr;
          break;
        case 'Load Per Day':
          value = entry.device_loads_per_day;
          break;
        case 'Hauler Load Time':
          value = isNaN(entry?.device_hauler_load_time)
            ? 0
            : Number(entry.device_hauler_load_time) / 60;
          break;
        case 'Loader Cycle Time':
          value = isNaN(entry.device_loader_cycle_time)
            ? 0
            : Number(entry.device_loader_cycle_time) / 60;
          break;
        default:
          value = 0;
      }

      return {
        date: entry.day,
        value: convertToTwoDecimalPlaces(`${value}`),
      };
    });

  const chartData = formattedChartData.sort((a, b) => {
    const dateA = dayjs(a.date).unix();
    const dateB = dayjs(b.date).unix();
    return dateA - dateB;
  });

  const format = useMemo(() => {
    return aggregation === 'Monthly' ? 'DD MMM' : DATE_FORMAT;
  }, [aggregation]);

  const formatTimestamp = (timestamp: string): string => {
    const formattedDate = convertStdDateString(timestamp, format);
    return formattedDate;
  };

  const validEntries = chartData.filter(
    (entry: any) =>
      entry.value !== null && !isNaN(entry.value) && entry.value !== 0
  );
  let average: number = 0;

  if (selectedSource.toLowerCase().replace(/ /g, '') === 'productionperhour') {
    // Filter only this asset
    const loaderDevices = devices.filter(
      (item) => item.loader === device.loader
    );

    // Total engine active minutes
    const totalEngineActiveMin = loaderDevices.reduce(
      (sum, entry) => sum + (entry.engine_active_minute || 0),
      0
    );

    const totalHours = totalEngineActiveMin / 60;

    // Total load only for this loader
    const totalLoad = loaderDevices.reduce(
      (sum, entry) => sum + (entry.total_load_for_selected_range || 0),
      0
    );

    // Final avg production per hour
    average = totalHours > 0 ? totalLoad / totalHours : 0;
  } else {
    average =
      validEntries.reduce(
        (sum: number, entry: any) => sum + parseFloat(entry.value),
        0
      ) / validEntries.length;
  }

  const maxValue = validEntries.length
    ? Math.max(...validEntries.map((entry) => parseFloat(entry.value)))
    : 0;

  return (
    <Box sx={{ paddingTop: '10px' }}>
      <FlexBox
        sx={{
          maxWidth: 'inherit',
          height,
          paddingTop: '20px',
          marginRight: '10px',
          paddingBottom: '10px',
        }}
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            width={700}
            height={300}
            data={chartData}
            margin={{
              top: 20,
              right: 100,
              left: 5,
              bottom: 5,
            }}
            barSize={30}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke={palette.cartesianGrid}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatTimestamp}
              tick={{ fontSize: 10 }}
            />
            <YAxis
              tick={{ fontSize: 10 }}
              domain={[0, Math.ceil(maxValue * 1.1)]}
              label={{
                value: ` ${
                  selectedSource.toLowerCase().replace(/ /g, '') ===
                  'haulerloadtime'
                    ? `${selectedSource} (mins)`
                    : selectedSource.toLowerCase().replace(/ /g, '') ===
                      'loadercycletime'
                    ? `${selectedSource} (mins)`
                    : `${selectedSource} ${dataUnit}`
                }`,
                angle: 270,
                position: 'insideLeft',
                fontSize: 10,
                textAnchor: 'middle',
                dy: 50,
              }}
            />
            <Tooltip
              cursor={{
                fill: enableDarkTheme
                  ? `${palette.primary}26`
                  : `${palette.primary}1a`,
              }}
              wrapperStyle={{ outline: 'none', fontSize: '11px' }}
              contentStyle={{
                backgroundColor: enableDarkTheme
                  ? 'rgba(0,0,0, 0.6)'
                  : 'rgba(255, 255, 255, 0.6)',
                borderColor: 'transparent',
                borderRadius: '4px',
              }}
              itemStyle={{
                color: enableDarkTheme
                  ? ThemePalette.typography.wheat
                  : ThemePalette.typography.black,
              }}
              labelFormatter={(value: string) =>
                convertStdDateString(value, format)
              }
            />
            <Bar
              dataKey="value"
              fill={palette.primary}
              activeBar={
                <Rectangle
                  fill={`${palette.primary}66`}
                  stroke={palette.primary}
                />
              }
            />
            <ReferenceLine
              y={average}
              label={{
                value:
                  selectedSource.toLowerCase().replace(/ /g, '') ===
                  'productionperhour'
                    ? `Avg ${average.toFixed(2)} ${dataUnit}`
                    : selectedSource.toLowerCase().replace(/ /g, '') ===
                        'haulerloadtime' ||
                      selectedSource.toLowerCase().replace(/ /g, '') ===
                        'loadercycletime'
                    ? `${formatNumberToShortScale(
                        truncateTo2dp(average.toString())
                      )} mins`
                    : `Avg ${average.toFixed(2)}  ${dataUnit}`,
                position: 'right',
                fontSize: '10px',
              }}
              stroke={palette.primary}
              strokeDasharray="3 3"
              isFront
            />
          </BarChart>
        </ResponsiveContainer>
      </FlexBox>
    </Box>
  );
};

export default AssetItemChart;
